import { lazy, Suspense, useEffect, useState, type ReactElement } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
} from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { queryClient } from './lib/query-client';
import { trackPageView } from './lib/tracking';
import { AuthProvider } from './lib/auth-context';
import { PageTitleProvider } from './lib/page-title';
import { LocaleProvider, LocaleServerSync, useLocale } from './i18n/locale-context';
import { DEFAULT_LOCALE, resolveLocaleFromPath, type Locale } from './i18n/index';
import { countryConfig } from './country';
import { AppLayout } from './components/app-layout';
import { PendingSavedSearchSync } from './components/pending-saved-search-sync';
import { TelegramBotLoginResume } from './components/telegram-bot-login-resume';
import { SavedRedirect } from './components/saved-redirect';
import { Protected } from './routes/_protected';
import { PageSpinner } from './components/ui/spinner';
import { lazyRoute } from './lib/lazy-route';
import { preloadableRoute } from './lib/preloadable-route';
import { localePath, stripLocalePrefix } from './i18n/locale-path';
import { catalogSectionServed, type CatalogSection } from '@tenders/shared';
import { CookieConsentBanner } from './components/cookie-consent-banner';
import { consentRequired } from './lib/cookie-consent';
import { ES_LEGAL_REPLACES, hasEsLegalSet } from './content/legal/es/paths';
import { expiringContractsOn } from '@tenders/shared';
// Public routes are PRERENDERED, so they must be statically imported to hydrate
// synchronously against the prerendered HTML (React.lazy would mismatch).
// The ONE exception is the /guide pair below — split out because the article
// corpus dwarfs the rest of the bundle — and it pays for that exception with an
// explicit preload before hydration; see `lib/preloadable-route.tsx`.
import { Catalog } from './routes/public/catalog';
import { TenderDetail } from './routes/public/tender-detail';
import { SupplierProfile } from './routes/public/supplier-profile';
import { BuyerProfile } from './routes/public/buyer-profile';
import { Prices } from './routes/public/prices';
import { Directory } from './routes/public/directory';
import { Companies } from './routes/public/companies';
import { MarketDashboard } from './routes/public/market';
import { PriceDetail } from './routes/public/price-detail';
import { Region } from './routes/public/region';
import { Category } from './routes/public/category';
import { Plans } from './routes/plans';
import { About } from './routes/about';
import { Offer } from './routes/legal/offer';
import { Privacy } from './routes/legal/privacy';
import { Requisites } from './routes/requisites';
import { NotFound, RouteError } from './routes/not-found';
// Landing pages are PRERENDERED too, and mount OUTSIDE AppLayout (they carry
// their own header/footer) — same static-import rule as the routes above.
import { LandingStart } from './routes/landing/start';
import { LandingIp } from './routes/landing/ip';
import type { RouteObject } from 'react-router';

// Client-only routes (auth, dashboard, invoice, billing return) are NEVER
// prerendered/SSR'd — code-split them so their weight drops out of the main chunk
// that the content (catalog/tender) pages parse before hydration. Named exports →
// map to a default for React.lazy.
// ⚠️ Each loader goes through `lazyRoute`, which reloads the page ONCE when the
// chunk 404s. A deploy renames these files and prunes the old release, so a tab
// opened beforehand asks for a bundle that no longer exists; without the retry
// the visitor lands on the route-error screen and reads it as "I cannot log in"
// — measured on prod 2026-09-15, see `lib/lazy-route.ts`.
const Login = lazy(
  lazyRoute('login', () => import('./routes/login').then((m) => ({ default: m.Login }))),
);
const Register = lazy(
  lazyRoute('register', () => import('./routes/register').then((m) => ({ default: m.Register }))),
);
const Dashboard = lazy(
  lazyRoute('dashboard', () =>
    import('./routes/dashboard').then((m) => ({ default: m.Dashboard })),
  ),
);
const Favorites = lazy(
  lazyRoute('favorites', () =>
    import('./routes/favorites').then((m) => ({ default: m.Favorites })),
  ),
);
const MyTenders = lazy(
  lazyRoute('my-tenders', () =>
    import('./routes/my-tenders').then((m) => ({ default: m.MyTenders })),
  ),
);
const Invoice = lazy(
  lazyRoute('invoice', () => import('./routes/invoice').then((m) => ({ default: m.Invoice }))),
);
const BillingReturn = lazy(
  lazyRoute('billing-return', () =>
    import('./routes/billing-return').then((m) => ({ default: m.BillingReturn })),
  ),
);
const Team = lazy(
  lazyRoute('team', () => import('./routes/team').then((m) => ({ default: m.Team }))),
);
const Join = lazy(
  lazyRoute('join', () => import('./routes/join').then((m) => ({ default: m.Join }))),
);
// `/auth/tg/<code>` — the Telegram bot's one-time «Перейти на сайт» link
// (2026-10-18). Standalone like /login, never prerendered, `noindex`.
const TelegramMagicLink = lazy(
  lazyRoute('telegram-magic', () =>
    import('./routes/telegram-magic').then((m) => ({ default: m.TelegramMagicLink })),
  ),
);
// `/zayavka` — the Meta Lead campaign's phone form. Lazy: it is an ad landing,
// never prerendered (noindex), so nothing has to hydrate against its markup and
// no other visitor should download it.
const Zayavka = lazy(
  lazyRoute('zayavka', () =>
    import('./routes/landing/zayavka').then((m) => ({ default: m.Zayavka })),
  ),
);
const PayLink = lazy(
  lazyRoute('pay-link', () => import('./routes/pay-link').then((m) => ({ default: m.PayLink }))),
);

/*
 * ── The /guide pair: code-split, and PRERENDERED ────────────────────────────
 *
 * `routes/guide` + `routes/guide-article` pull `content/guide-articles/**` —
 * 39 articles × ru/uz, ~2 MB of source text — and while they were statically
 * imported every visitor of every page downloaded and parsed all of it inside
 * `main-*.js` (2,728 kB / 728 kB gzip, measured on gl/main 2026-09-26), to render
 * a catalog page that links to none of it.
 *
 * They CANNOT use `lazy()` + `lz()` like the signed-in routes above: both are
 * prerendered, and a Suspense boundary that exists only on the client cannot
 * hydrate the prerendered HTML (no `<!--$-->` markers in `renderToString`
 * output). `preloadableRoute` instead renders the chunk synchronously once it is
 * loaded, and `preloadRouteChunk` below loads it BEFORE `hydrateRoot` for the
 * path being hydrated — the long version is in `lib/preloadable-route.tsx`.
 *
 * `entry-server.tsx` keeps its STATIC imports of both routes on purpose: the SSR
 * bundle is built separately (`dist-ssr`, never served to a browser), so nothing
 * about the prerendered HTML changes — the guide pages still render server-side
 * with their full body, title and OG tags.
 */
const Guide = preloadableRoute('guide', () =>
  import('./routes/guide').then((m) => ({ default: m.Guide })),
);
const GuideArticle = preloadableRoute('guide-article', () =>
  import('./routes/guide-article').then((m) => ({ default: m.GuideArticle })),
);

/**
 * MiLicita (`es`) legal set — /aviso-legal, /condiciones, /privacidad, /cookies.
 * Prerendered there, so preloadable like the guide pair (one shared chunk), and
 * code-split so the Spanish text never weighs on the UZ/KZ bundle. On UZ/KZ none
 * of this mounts and `/offer`, `/privacy`, `/requisites` stay exactly as they are.
 */
const ES_LEGAL = hasEsLegalSet(countryConfig.code);
const loadEsLegal = () => import('./routes/legal/es-legal');
const EsAvisoLegal = preloadableRoute('es-aviso-legal', () =>
  loadEsLegal().then((m) => ({ default: m.AvisoLegal })),
);
const EsCondiciones = preloadableRoute('es-condiciones', () =>
  loadEsLegal().then((m) => ({ default: m.Condiciones })),
);
const EsPrivacidad = preloadableRoute('es-privacidad', () =>
  loadEsLegal().then((m) => ({ default: m.Privacidad })),
);
const EsCookies = preloadableRoute('es-cookies', () =>
  loadEsLegal().then((m) => ({ default: m.PoliticaCookies })),
);
/**
 * «Próximos vencimientos» (`/vencimientos`) — Spain only
 * (`features.expiringContracts`). Prerendered there, so preloadable; code-split
 * so it never weighs on the UZ/KZ bundle, where the route does not exist at all.
 */
const EXPIRING_ON = expiringContractsOn(countryConfig);
const ExpiringContractsPage = preloadableRoute('expiring-contracts', () =>
  import('./routes/public/expiring-contracts').then((m) => ({ default: m.ExpiringContracts })),
);

const ES_LEGAL_ROUTE_CHUNKS: Record<string, typeof EsAvisoLegal> = {
  '/aviso-legal': EsAvisoLegal,
  '/condiciones': EsCondiciones,
  '/privacidad': EsPrivacidad,
  '/cookies': EsCookies,
};

/**
 * The legal routes of this deployment. UZ/KZ: the оферта, the privacy policy and
 * the requisites, unchanged. `es`: the Spanish set, and the three UZ/KZ paths
 * redirect to their Spanish counterpart — the Uzbek documents and the Uzbek
 * operator's requisites must never render under milicita.es.
 */
function legalChildren(): RouteObject[] {
  if (!ES_LEGAL) {
    return [
      { path: 'offer', element: <Offer /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'requisites', element: <Requisites /> },
    ];
  }
  return [
    { path: 'aviso-legal', element: <EsAvisoLegal /> },
    { path: 'condiciones', element: <EsCondiciones /> },
    { path: 'privacidad', element: <EsPrivacidad /> },
    { path: 'cookies', element: <EsCookies /> },
    ...Object.entries(ES_LEGAL_REPLACES).map(([from, to]) => ({
      path: from.slice(1),
      element: <Navigate to={to} replace />,
    })),
  ];
}

/**
 * Resolve the code-split chunk a PRERENDERED path needs before React mounts, so
 * the first client render reproduces the prerendered markup instead of a spinner.
 * Returns `null` when the path needs nothing — which is every path except the
 * two guide routes, and those callers mount synchronously exactly as before.
 *
 * ⚠️ Making another prerendered route lazy means adding it here too. Forgetting
 * that does not break the page — it hydration-mismatches and silently
 * client-renders it, throwing away the prerendered DOM. Guarded by
 * `app.guide-preload.test.tsx`.
 */
export function preloadRouteChunk(pathname: string): Promise<void> | null {
  // Drop a trailing slash FIRST. react-router ignores it when matching, so
  // `/guide/` mounts the hub — but `startsWith('/guide/')` reads it as an
  // article and would preload the wrong chunk, leaving the route it actually
  // mounts unresolved. Measured in a real browser on 2026-09-26: `/guide/`
  // then hydrated with React error #418 and re-rendered the hub client-side,
  // which is precisely the failure this whole mechanism exists to avoid. The
  // prerender writes `<path>/index.html`, so a trailing slash is a live URL.
  const bare = stripLocalePrefix(pathname).replace(/\/+$/, '') || '/';
  if (bare === '/guide') return Guide.preload();
  if (bare.startsWith('/guide/')) return GuideArticle.preload();
  const esLegalChunk = ES_LEGAL ? ES_LEGAL_ROUTE_CHUNKS[bare] : undefined;
  if (esLegalChunk) return esLegalChunk.preload();
  if (EXPIRING_ON && bare === '/vencimientos') return ExpiringContractsPage.preload();
  return null;
}

/** The catalogue home of the current locale, as a redirect. */
function ToCatalogHome() {
  const { locale } = useLocale();
  return <Navigate to={localePath('/', locale)} replace />;
}

/**
 * `/ifi` and `/supply` — the catalogue's side sections — exist only where the
 * country serves them (`features.sections`; absent = both, so UZ and KZ mount
 * exactly what they always did). Spain has neither: a guessed or old link lands
 * on the catalogue instead of a page of World Bank / ADB / UzRD chips.
 */
function sectionRoute(section: CatalogSection): ReactElement {
  return catalogSectionServed(countryConfig, section) ? (
    <Catalog scope={section} />
  ) : (
    <ToCatalogHome />
  );
}

/**
 * `/market`, `/prices` and `/prices/:ikpu` are built on the ИКПУ taxonomy and the
 * price history (`features.priceIntel`: UZ and KZ on, unchanged). Where it is off
 * (Spain) they rendered anyway — «NUEVAS ESTA SEMANA 12.344» over Uzbek-shaped
 * blocks on milicita.es (global review 21.10, cosmetics #8). They are never
 * prerendered or sitemapped there (`PRICE_INTEL_ROUTES` in scripts/prerender.ts);
 * now the router answers them with the 404 page, which is `noindex`.
 */
function priceIntelRoute(element: ReactElement): ReactElement {
  return countryConfig.features.priceIntel ? element : <NotFound />;
}

/** Wrap a lazily-loaded route element in a Suspense boundary (brief spinner while
 *  the split chunk loads on client navigation). */
const lz = (el: ReactElement): ReactElement => <Suspense fallback={<PageSpinner />}>{el}</Suspense>;

// Public children built with RELATIVE paths so the SAME set mounts cleanly under
// both `/` (ru, default) and `/uz` (Uzbek). The home route is `index: true`.
function publicChildren(): RouteObject[] {
  return [
    { index: true, element: <Catalog /> }, // home IS the catalog
    { path: 'catalog', element: <Catalog /> },
    { path: 'ifi', element: sectionRoute('ifi') },
    // Task 10 — cooperation.uz reverse-auction supply requests: a separate,
    // opt-in, deliberately `noindex` section (thin, budgetless leads). See
    // catalog.tsx's `scope === 'supply'` branch and lib/tender-status.ts.
    { path: 'supply', element: sectionRoute('supply') },
    // ── Crawlable pagination ─────────────────────────────────────────────────
    // `?page=N` cannot be prerendered: the prerender writes files by PATH, and
    // nginx `try_files` resolves `/?page=2` to the SAME index.html as `/`. Live
    // check on 2026-08-01: `/` and `/?page=2` were byte-identical (146,881 bytes,
    // the same 20 tender links). So query-string paging was not a degraded crawl
    // path — it was no crawl path at all, and every listing past the first 20
    // items existed only in the sitemap. These path routes give each page a real
    // URL that can be prerendered, linked and indexed.
    { path: 'page/:page', element: <Catalog /> },
    { path: 'region/:slug/page/:page', element: <Region /> },
    { path: 'category/:slug/page/:page', element: <Category /> },
    { path: 'companies/page/:page', element: <Companies /> },
    // ── Outcomes ────────────────────────────────────────────────────────────
    // The awarded-lot archive (`status=closed&awarded=true`) — ~3,981 lots that
    // are deliberately indexable (isTenderIndexable: a finished lot earns its
    // page once it carries a winner) and are in the sitemap, but had NO inbound
    // internal link from anywhere: browse defaults to active+closing, /market
    // and /prices/:ikpu link to zero tenders, and tender pages link to zero
    // siblings. That orphaned set (~7,962 URLs across both locales) closely
    // matches the 9,036 "Discovered – currently not indexed" in GSC. This is
    // their browse path.
    { path: 'results', element: <Catalog scope="awarded" /> },
    { path: 'results/page/:page', element: <Catalog scope="awarded" /> },
    // «Próximos vencimientos» — only where the country offers it (Spain). Absent
    // on UZ/KZ, where `/vencimientos` stays the catch-all 404 it always was.
    ...(EXPIRING_ON ? [{ path: 'vencimientos', element: <ExpiringContractsPage /> }] : []),
    { path: 'tender/:id', element: <TenderDetail /> },
    { path: 'supplier/:tin', element: <SupplierProfile /> },
    { path: 'buyer/:tin', element: <BuyerProfile /> },
    { path: 'prices', element: priceIntelRoute(<Prices />) },
    { path: 'market', element: priceIntelRoute(<MarketDashboard />) },
    { path: 'prices/:ikpu', element: priceIntelRoute(<PriceDetail />) },
    // The hub-of-hubs. 43 of the 45 category/region hubs were orphaned before this
    // page existed — see the note in directory.tsx.
    { path: 'directory', element: <Directory /> },
    { path: 'companies', element: <Companies /> },
    { path: 'region/:slug', element: <Region /> },
    { path: 'category/:slug', element: <Category /> },
    { path: 'plans', element: <Plans /> },
    { path: 'about', element: <About /> },
    { path: 'guide', element: <Guide /> },
    { path: 'guide/:slug', element: <GuideArticle /> },
    ...legalChildren(),
    { path: 'billing/return', element: lz(<BillingReturn />) },
    /*
     * «Оплатить с компьютера» — the other end of the one-shot pay link. It sits
     * INSIDE `publicChildren` so it mounts under both `/` and `/uz`: the link is
     * built with the buyer's own locale prefix, and a uz buyer must not be
     * dropped into a Russian page at the payment step. Public, and deliberately
     * so — it is opened by a laptop that was never signed in here; the token in
     * the path is the whole credential.
     */
    { path: 'pay/:token', element: lz(<PayLink />) },
    // Catch-all — LAST for readability only. react-router ranks matches by
    // specificity (static segment 10 > dynamic 3 > splat -2) and falls back to
    // declaration order only to break a tie, so this can never out-rank a real
    // route above it, nor the standalone `login`/`join/:token`/`schet` routes.
    // It lives INSIDE `publicChildren` so it mounts in BOTH trees: `/<typo>`
    // and `/uz/<typo>`, the latter keeping the visitor in Uzbek.
    { path: '*', element: <NotFound /> },
  ];
}

function protectedChildren(): RouteObject[] {
  return [
    {
      element: <Protected />,
      children: [
        { index: true, element: lz(<Dashboard />) },
        { path: 'favorites', element: lz(<Favorites />) },
        { path: 'alerts', element: lz(<Dashboard />) },
        // «Мои тендеры» — the saved-search match ledger. Self-gating: with
        // fewer than two active searches it redirects to /app/alerts rather
        // than rendering a worse copy of the catalog.
        { path: 'my-tenders', element: lz(<MyTenders />) },
        { path: 'team', element: lz(<Team />) },
      ],
    },
    // `/app/saved` is retired to a redirect (see SavedRedirect) and MUST
    // resolve for a signed-OUT visitor too — a live Telegram command shipped
    // this URL in messages that have already been delivered, including to
    // recipients not signed in on the device they tap it from. Deliberately
    // a SIBLING of the <Protected> group above, not nested inside it: nesting
    // would send a guest through Protected's `/login` bounce (with no
    // return-to) instead of straight to the public home.
    { path: 'saved', element: <SavedRedirect /> },
  ];
}

/**
 * Give every top-level route the styled error boundary. There is no single root
 * route to hang one off, so it goes on each branch; a route that declares its
 * own `errorElement` keeps it. Without this, a render error anywhere drops the
 * visitor onto react-router's default developer screen.
 */
function withErrorBoundary(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => ({ ...route, errorElement: route.errorElement ?? <RouteError /> }));
}

/**
 * Window-level scroll reset/restoration for the routes that are NOT children of
 * `AppLayout` — login, register, /start, /ip, /schet, /join. Those render their
 * own `min-h-screen` page and scroll the WINDOW, and nothing reset it: measured
 * on prod 2026-09-16, /register scrolled to 345px then «Войти» opened /login at
 * 174px (= 594 − 420, the shorter page's max, i.e. the old offset clamped) —
 * the new page opens at the bottom and the visitor scrolls it from there.
 *
 * Inside the shell this is a no-op by construction: AppLayout pins the grid to
 * 100dvh, so window scroll is permanently 0 and the inner `<main>` stays owned
 * by useScrollResetOnNavigate / useScrollRestoration. Different scrollers, so
 * the two mechanisms cannot contend. Back/forward keep native behaviour
 * (ScrollRestoration restores the saved window offset on POP).
 */
function ScrollManager() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

// Mount the whole tree twice: bare (ru) and under `/uz`. Auth/standalone routes
// are mirrored too so a uz user never falls out of their language.
//
// The `/uz/*` half is gated on `countryConfig.locales.includes('uz')` — on a
// deployment that doesn't serve uz (KZ: `locales: ['ru']`), `scripts/prerender.ts`
// never generates a `/uz/*` shell and `seo.tsx`'s x-default no longer points at
// one, but this router used to mount the subtree anyway: paste-in-the-URL-bar
// reachable, rendering the (unconditionally-imported) uz i18n catalog inside the
// app chrome of a Russian-only product. UZ ships both locales, so `hasUz` is
// `true` there and this list is unchanged.
// Every NON-DEFAULT locale gets its own `/{locale}/*` subtree (the default locale
// owns the bare paths). UZ → ['uz'], KZ → ['kk']. This used to hardcode only
// '/uz/*', so on KZ (locales ['ru','kk']) every '/kk/*' path 404'd — no matching
// route — even though the kk shell + i18n rendered: the prerender emitted the
// NotFound page for '/kk/privacy' etc. and the client router did the same.
// `start/ip` recovers a malformed Meta-ad URL (there is `/start` and `/ip`, no
// nesting) to the real landing before the `*` catch-all can 404 it.
const prefixLocales = countryConfig.locales.filter((l) => l !== countryConfig.defaultLocale);

/**
 * `/zayavka` (+998 phone, the owner's Telegram) exists on the UZ deployment
 * only. Standalone like `/start`, with NO `entry-server.tsx` twin and no
 * `STATIC_ROUTES` entry: it is never prerendered and never enters the sitemap
 * — an ad destination, served `noindex` by the SPA fallback and by its own head.
 */
function zayavkaRoutes(path: string): RouteObject[] {
  return countryConfig.code === 'uz' ? [{ path, element: lz(<Zayavka />) }] : [];
}

/**
 * `/start` and `/ip` — the conversion landings. Served only where the country
 * has landing copy (`features.conversionLandings`): the only copy that exists is
 * Uzbek («Все тендеры Узбекистана», prices in so'm, Payme, the +998 phone), and
 * on Kazakhstan it was served — and prerendered, and sitemapped — under
 * kaztender.com. Where it is off, the paths redirect to the catalogue instead of
 * 404-ing, so a stray ad or shared link still lands somewhere useful.
 *
 * `base` is '' for the bare (default-locale) tree and the locale for a prefixed one.
 */
function landingRoutes(base: string): RouteObject[] {
  const at = (path: string): string => (base ? `${base}/${path}` : path);
  const home = base ? `/${base}` : '/';
  if (!countryConfig.features.conversionLandings) {
    return ['start', 'start/ip', 'ip'].map((path) => ({
      path: at(path),
      element: <Navigate to={home} replace />,
    }));
  }
  return [
    { path: at('start'), element: <LandingStart /> },
    { path: at('start/ip'), element: <Navigate to={`/${at('start')}`} replace /> },
    { path: at('ip'), element: <LandingIp /> },
  ];
}

/**
 * `/auth/tg` — the Telegram bot's one-time sign-in link — only where the country
 * runs a bot (`messaging.telegram`: UZ and KZ, unchanged). Spain has none yet;
 * there the page told a visitor to «entrar con Telegram: es un solo botón» about
 * a button that does not exist, so the path goes to the login page instead.
 */
function telegramMagicRoute(login: string): ReactElement {
  return countryConfig.messaging.telegram ? (
    lz(<TelegramMagicLink />)
  ) : (
    <Navigate to={login} replace />
  );
}

/**
 * `/schet` — the printable bank invoice. Kazakhstan sells no payment by invoice
 * (`payments.bankInvoice`, owner 2026-10-13), so there a bookmarked or guessed
 * link lands on the tariffs instead of an Uzbek счёт in so'm.
 */
function invoiceRoute(plans: string) {
  return countryConfig.payments.bankInvoice ? lz(<Invoice />) : <Navigate to={plans} replace />;
}

function localePrefixedRoutes(loc: string): RouteObject[] {
  return [
    { path: loc, element: <AppLayout />, children: publicChildren() },
    { path: `${loc}/schet`, element: invoiceRoute(`/${loc}/plans`) },
    ...landingRoutes(loc),
    ...zayavkaRoutes(`${loc}/zayavka`),
    // Legal aliases — see the bare pair below for why they exist. The target
    // keeps the locale prefix so a uz reader is not dropped into the ru document.
    { path: `${loc}/oferta`, element: <Navigate to={`/${loc}/offer`} replace /> },
    { path: `${loc}/terms`, element: <Navigate to={`/${loc}/offer`} replace /> },
    { path: `${loc}/login`, element: lz(<Login />) },
    { path: `${loc}/register`, element: lz(<Register />) },
    { path: `${loc}/auth/tg/:code`, element: telegramMagicRoute(`/${loc}/login`) },
    { path: `${loc}/auth/tg`, element: telegramMagicRoute(`/${loc}/login`) },
    // Standalone (not under AppLayout), see the bare `join` pair below for why.
    { path: `${loc}/join/:token`, element: lz(<Join />) },
    { path: `${loc}/join`, element: lz(<Join />) },
    { path: `${loc}/app`, element: <AppLayout />, children: protectedChildren() },
  ];
}

const routeTree: RouteObject[] = withErrorBoundary([
  { element: <AppLayout />, children: publicChildren() }, // ru (bare)
  { path: 'schet', element: invoiceRoute('/plans') },
  ...landingRoutes(''),
  ...zayavkaRoutes('zayavka'),
  // Legal aliases. All four URLs are live in the wild and all four resolved to
  // nothing: `/oferta` and `/terms` are what older email footers and ad copy
  // link, `/ru/offer` / `/ru/privacy` date from before ru became the DEFAULT
  // locale and gave up its prefix. nginx's SPA fallback answers each with a 200
  // and an empty shell, so they read as a working page to a crawler and as a
  // blank one to a buyer following a link out of an invoice.
  // Same shape as `start/ip` above — a client-side <Navigate> with no
  // `entry-server.tsx` twin and no `STATIC_ROUTES` entry, so an alias is never
  // prerendered and never enters the sitemap; only `/offer` and `/privacy` are.
  { path: 'oferta', element: <Navigate to="/offer" replace /> },
  { path: 'terms', element: <Navigate to="/offer" replace /> },
  { path: 'ru/offer', element: <Navigate to="/offer" replace /> },
  { path: 'ru/privacy', element: <Navigate to="/privacy" replace /> },
  { path: 'login', element: lz(<Login />) },
  { path: 'register', element: lz(<Register />) },
  // The bot's magic link. The bare `auth/tg` is the URL the visitor actually
  // sits on: index.html lifts the code out of the address bar before any
  // analytics reads it (same idea as the `join` pair below).
  { path: 'auth/tg/:code', element: telegramMagicRoute('/login') },
  { path: 'auth/tg', element: telegramMagicRoute('/login') },
  // The BARE join pair is required, not cosmetic: Join erases the token from the
  // address bar via replaceState, so `/join` is the URL the visitor actually sits
  // on for the rest of the flow — a mobile background-tab refresh must resume it
  // (Join reads the token back out of its sessionStorage stash) rather than 404.
  { path: 'join/:token', element: lz(<Join />) },
  { path: 'join', element: lz(<Join />) },
  { path: 'app', element: <AppLayout />, children: protectedChildren() },
  ...prefixLocales.flatMap(localePrefixedRoutes),
]);

// One pathless root so <ScrollRestoration/> renders exactly once, inside the
// router, above every branch. It carries its own errorElement so a render
// error in ScrollManager itself still lands on the styled boundary.
export const appRoutes: RouteObject[] = [
  { element: <ScrollManager />, errorElement: <RouteError />, children: routeTree },
];

const router = createBrowserRouter(appRoutes);

export function App() {
  // The URL is authoritative for locale. Derive it from the current location and
  // keep it in sync on every navigation via the router subscription.
  const [locale, setLocale] = useState<Locale>(() =>
    typeof window !== 'undefined'
      ? resolveLocaleFromPath(window.location.pathname)
      : DEFAULT_LOCALE,
  );

  useEffect(() => {
    let last = router.state.location.pathname;
    setLocale(resolveLocaleFromPath(last));
    return router.subscribe((state) => {
      if (state.navigation.state !== 'idle') return;
      const p = state.location.pathname;
      setLocale(resolveLocaleFromPath(p));
      if (p !== last) {
        last = p;
        trackPageView(p); // Meta Pixel PageView (GA4 counts SPA nav itself)
      }
    });
  }, []);

  return (
    <HelmetProvider>
      <LocaleProvider locale={locale}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LocaleServerSync />
            <PendingSavedSearchSync />
            <TelegramBotLoginResume />
            <PageTitleProvider>
              <RouterProvider router={router} />
            </PageTitleProvider>
            {/* Cookie-consent deployments only (MiLicita). Client-only: renders
                nothing until after hydration, and nothing at all on UZ/KZ. */}
            {consentRequired() && <CookieConsentBanner />}
          </AuthProvider>
        </QueryClientProvider>
      </LocaleProvider>
    </HelmetProvider>
  );
}
